import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { account } from "../account.ts";
import type { SignedIn } from "../store.ts";
import { AuthPage, authMessage } from "./Login.tsx";

/** A new business with its owner; fields and rules as in the sister apps. */
export function Signup({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [open, setOpen] = useState<boolean | null>(null);
  const [form, setForm] = useState({ workspace: "", displayName: "", email: "", username: "", password: "", repeat: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    account.signupOpen().then(setOpen, () => setOpen(false));
  }, []);

  const field = (key: keyof typeof form) => ({ value: form[key], onChange: (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value }) });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (form.password.length < 10) return setError("Das Passwort braucht mindestens 10 Zeichen.");
    if (form.password !== form.repeat) return setError("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      const { repeat: _repeat, ...body } = form;
      onSignedIn(await account.signup({ ...body, username: body.username.trim() }));
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (open === false) {
    return (
      <AuthPage>
        <div className="card">
          <h2>Neu anlegen</h2>
          <p>Neue Betriebe legt hier gerade nur der Betreiber an. Gehörst du zu einem Betrieb, der die App schon nutzt, bekommst du vom Inhaber eine Einladung.</p>
          <a className="button" href="#/anmelden">
            Zur Anmeldung
          </a>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Neuen Betrieb anlegen</h2>
        <p className="muted">Du legst den Betrieb an und bist sein Inhaber. Was ihr eintragt, sieht nur, wer dazugehört.</p>
        <form noValidate onSubmit={submit}>
          <label>
            Name des Betriebs
            <input {...field("workspace")} maxLength={60} autoComplete="organization" placeholder="z. B. Tapas-Bar Pit" data-testid="workspace" />
          </label>
          <label>
            Dein Name
            <input {...field("displayName")} maxLength={40} autoComplete="nickname" data-testid="display-name" />
            <span className="hint">So nennen dich die anderen in der App, etwa Pit.</span>
          </label>
          <label>
            E-Mail-Adresse (Pflicht)
            <input {...field("email")} type="email" required maxLength={200} autoComplete="email" placeholder="name@beispiel.de" data-testid="email" />
            <span className="hint">Für den Link, falls du dein Passwort vergisst. Nie für Werbung. Später jederzeit änderbar.</span>
          </label>
          <label>
            Benutzername
            <input {...field("username")} maxLength={64} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="new-username" />
            <span className="hint">Zum Anmelden. Darf auch deine E-Mail-Adresse sein.</span>
          </label>
          <label>
            Passwort
            <input {...field("password")} type="password" autoComplete="new-password" data-testid="new-password" />
            <span className="hint">Mindestens 10 Zeichen.</span>
          </label>
          <label>
            Passwort wiederholen
            <input {...field("repeat")} type="password" autoComplete="new-password" data-testid="repeat-password" />
          </label>
          {error && <p className="error-box">{error}</p>}
          <button className="primary" disabled={busy || open === null} data-testid="signup">
            {busy ? "Betrieb anlegen …" : "Betrieb anlegen"}
          </button>
        </form>
        <p className="center">
          <a href="#/anmelden">Ich habe schon ein Konto</a>
        </p>
      </div>
    </AuthPage>
  );
}
